// hooks/useSocket.ts
import { useCallback, useEffect, useRef, useState } from "react";
// import { Node, Edge, Viewport } from "@xyflow/react";
import { throttle } from "lodash";
import { useAppSelector } from "@/app/hooks";
import {
  CursorPosition,
  WebSocketMessage,
  NodeEditingState,
  EmojiReaction,
} from "../types";

export const useSocket = (
  sprintId: string,
  sprintName: string,
  parentData?: any
) => {
  const [socket, setSocket] = useState<WebSocket | null>(null);
  const [connected, setConnected] = useState(false);
  const [cursors, setCursors] = useState<Map<string, CursorPosition>>(
    new Map()
  );
  const [users, setUsers] = useState<
    Map<string, { name: string; color: string }>
  >(new Map());
  const [draggingNodes, setDraggingNodes] = useState<
    Map<
      string,
      { nodeId: string; position: { x: number; y: number }; userId: string }
    >
  >(new Map());
  const [activeConnections, setActiveConnections] = useState<
    Map<
      string,
      {
        from: { x: number; y: number };
        to: { x: number; y: number };
        userId: string;
      }
    >
  >(new Map());
  const [editingNodes, setEditingNodes] = useState<
    Map<string, NodeEditingState>
  >(new Map());

  const socketRef = useRef<WebSocket | null>(null);
  const eventListeners = useRef<Map<string, Function[]>>(new Map());
  const cleanupTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Get current user from Redux store
  // const currentUser = useAppSelector((state: any) => state.auth?.currentUser);
  const currentUser = parentData?.currentUser;

  // Get user details from Redux or localStorage
  const userId = useRef(
    currentUser?._id ||
      JSON.parse(localStorage.getItem("currentUser") || "{}")?._id ||
      `user-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
  );

  const userName = useRef(
    currentUser?.fullName ||
      JSON.parse(localStorage.getItem("currentUser") || "{}")?.fullName ||
      `User-${Math.random().toString(36).substr(2, 4)}`
  );

  // Generate user color (stable per user ID)
  const userColor = useRef(() => {
    let hash = 0;
    const userIdStr = userId.current;
    for (let i = 0; i < userIdStr.length; i++) {
      hash = userIdStr.charCodeAt(i) + ((hash << 5) - hash);
    }
    const hue = Math.abs(hash % 360);
    return `hsl(${hue}, 70%, 50%)`;
  });

  const userColorValue = userColor.current();

  // Update user details when currentUser changes
  useEffect(() => {
    const newUserId =
      currentUser?._id ||
      JSON.parse(localStorage.getItem("currentUser") || "{}")?._id ||
      userId.current;

    const newUserName =
      currentUser?.fullName ||
      JSON.parse(localStorage.getItem("currentUser") || "{}")?.fullName ||
      userName.current;

    userId.current = newUserId;
    userName.current = newUserName;
  }, [currentUser]);

  // Throttled cursor position sender
  const sendCursorPosition = useRef(
    throttle((position: { x: number; y: number }) => {
      if (socketRef.current?.readyState === WebSocket.OPEN) {
        const message: WebSocketMessage = {
          action: "sendMessage",
          sprintId,
          type: "CURSOR_MOVED",
          payload: {
            x: position.x,
            y: position.y,
            userId: userId.current,
            userName: userName.current,
            color: userColorValue,
          },
        };
        socketRef.current.send(JSON.stringify(message));
      }
    }, 50)
  );

  // Throttled node drag position sender
  const sendNodeDragPosition = useRef(
    throttle((nodeId: string, position: { x: number; y: number }) => {
      if (socketRef.current?.readyState === WebSocket.OPEN) {
        const message: WebSocketMessage = {
          action: "sendMessage",
          sprintId,
          type: "NODE_DRAGGING",
          payload: {
            nodeId,
            position,
            userId: userId.current,
          },
        };
        socketRef.current.send(JSON.stringify(message));
      }
    }, 16)
  );

  const sendTextChange = useRef(
    throttle((nodeId: string, text: string) => {
      if (socketRef.current?.readyState === WebSocket.OPEN) {
        const message: WebSocketMessage = {
          action: "sendMessage",
          sprintId,
          type: "NODE_TEXT_CHANGED",
          payload: {
            nodeId,
            text,
            userId: userId.current,
          },
        };
        socketRef.current.send(JSON.stringify(message));
      }
    }, 100)
  );

  // Send connection preview
  const sendConnectionPreview = useCallback(
    (from: { x: number; y: number }, to: { x: number; y: number }) => {
      if (socketRef.current?.readyState === WebSocket.OPEN) {
        const message: WebSocketMessage = {
          action: "sendMessage",
          sprintId,
          type: "CONNECTION_PREVIEW",
          payload: {
            from,
            to,
            userId: userId.current,
          },
        };
        socketRef.current.send(JSON.stringify(message));

        // Set a failsafe cleanup timeout
        if (cleanupTimeoutRef.current) {
          clearTimeout(cleanupTimeoutRef.current);
        }

        cleanupTimeoutRef.current = setTimeout(() => {
          endConnectionPreview();
        }, 5000);
      }
    },
    [sprintId]
  );

  // End connection preview
  const endConnectionPreview = useCallback(() => {
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      const message: WebSocketMessage = {
        action: "sendMessage",
        sprintId,
        type: "CONNECTION_PREVIEW_END",
        payload: {
          userId: userId.current,
        },
      };
      socketRef.current.send(JSON.stringify(message));
    }

    // Clear the cleanup timeout
    if (cleanupTimeoutRef.current) {
      clearTimeout(cleanupTimeoutRef.current);
      cleanupTimeoutRef.current = null;
    }

    // Also clean up local state
    setActiveConnections((prev) => {
      const newMap = new Map(prev);
      newMap.delete(userId.current);
      return newMap;
    });
  }, [sprintId]);

  // Start node editing
  const startNodeEditing = useCallback(
    (nodeId: string, editType: "text" | "color" | "general") => {
      if (socketRef.current?.readyState === WebSocket.OPEN) {
        const message: WebSocketMessage = {
          action: "sendMessage",
          sprintId,
          type: "NODE_EDIT_START",
          payload: {
            nodeId,
            userId: userId.current,
            userName: userName.current,
            userColor: userColorValue,
            editType,
          },
        };
        socketRef.current.send(JSON.stringify(message));
      }
    },
    [sprintId, userColorValue]
  );

  // End node editing
  const endNodeEditing = useCallback(
    (nodeId: string) => {
      if (socketRef.current?.readyState === WebSocket.OPEN) {
        const message: WebSocketMessage = {
          action: "sendMessage",
          sprintId,
          type: "NODE_EDIT_END",
          payload: {
            nodeId,
            userId: userId.current,
          },
        };
        socketRef.current.send(JSON.stringify(message));
      }
    },
    [sprintId]
  );

  // ADD THESE: Emoji reaction handlers
  const sendEmojiReactionAdd = useCallback(
    (nodeId: string, reaction: EmojiReaction) => {
      if (socketRef.current?.readyState === WebSocket.OPEN) {
        const message: WebSocketMessage = {
          action: "sendMessage",
          sprintId,
          type: "EMOJI_REACTION_ADDED",
          payload: {
            nodeId,
            reaction,
            addedBy: userId.current,
          },
        };
        socketRef.current.send(JSON.stringify(message));
      }
    },
    [sprintId]
  );

  const sendEmojiReactionRemove = useCallback(
    (nodeId: string, emoji: string, reactionUserId: string) => {
      if (socketRef.current?.readyState === WebSocket.OPEN) {
        const message: WebSocketMessage = {
          action: "sendMessage",
          sprintId,
          type: "EMOJI_REACTION_REMOVED",
          payload: {
            nodeId,
            emoji,
            userId: reactionUserId,
            removedBy: userId.current,
          },
        };
        socketRef.current.send(JSON.stringify(message));
      }
    },
    [sprintId]
  );

  const sendCommentAdd = useCallback(
    (nodeId: string, comment: Comment) => {
      if (socketRef.current?.readyState === WebSocket.OPEN) {
        const message: WebSocketMessage = {
          action: "sendMessage",
          sprintId,
          type: "COMMENT_ADDED",
          payload: {
            nodeId,
            comment,
            addedBy: userId.current,
          },
        };
        socketRef.current.send(JSON.stringify(message));
      }
    },
    [sprintId]
  );

  const sendCommentReactionAdd = useCallback(
    (nodeId: string, commentId: string, reaction: CommentReaction) => {
      if (socketRef.current?.readyState === WebSocket.OPEN) {
        const message: WebSocketMessage = {
          action: "sendMessage",
          sprintId,
          type: "COMMENT_REACTION_ADDED",
          payload: {
            nodeId,
            commentId,
            reaction,
            addedBy: userId.current,
          },
        };
        socketRef.current.send(JSON.stringify(message));
      }
    },
    [sprintId]
  );

  const sendCommentReactionRemove = useCallback(
    (
      nodeId: string,
      commentId: string,
      emoji: string,
      reactionUserId: string
    ) => {
      if (socketRef.current?.readyState === WebSocket.OPEN) {
        const message: WebSocketMessage = {
          action: "sendMessage",
          sprintId,
          type: "COMMENT_REACTION_REMOVED",
          payload: {
            nodeId,
            commentId,
            emoji,
            userId: reactionUserId,
            removedBy: userId.current,
          },
        };
        socketRef.current.send(JSON.stringify(message));
      }
    },
    [sprintId]
  );

  // Connect to WebSocket
  useEffect(() => {
    if (!sprintId) return;

    const wsUrl = `wss://unj54so163.execute-api.us-east-1.amazonaws.com/dev?sprintId=${sprintId}`;
    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      setConnected(true);
      setSocket(ws);
      socketRef.current = ws;

      // Add yourself to the users map immediately
      setUsers(
        (prev) =>
          new Map(
            prev.set(userId.current, {
              name: userName.current,
              color: userColorValue,
            })
          )
      );

      // Send initial join event
      const joinMessage: WebSocketMessage = {
        action: "sendMessage",
        sprintId,
        type: "USER_JOINED",
        payload: {
          userId: userId.current,
          userName: userName.current,
          color: userColorValue,
        },
      };
      ws.send(JSON.stringify(joinMessage));

      // Request current users list from existing users
      const requestUsersMessage: WebSocketMessage = {
        action: "sendMessage",
        sprintId,
        type: "REQUEST_CURRENT_USERS",
        payload: {
          userId: userId.current,
        },
      };
      ws.send(JSON.stringify(requestUsersMessage));
    };

    ws.onmessage = (event) => {
      try {
        const message: WebSocketMessage = JSON.parse(event.data);
        const { type, payload } = message;

        // Handle different event types
        switch (type) {
          case "CURSOR_MOVED":
            if (payload.userId !== userId.current) {
              setCursors((prev) => new Map(prev.set(payload.userId, payload)));
            }
            break;

          case "REQUEST_CURRENT_USERS":
            if (payload.userId !== userId.current) {
              // Send your user info to the requesting user
              const responseMessage: WebSocketMessage = {
                action: "sendMessage",
                sprintId,
                type: "USER_PRESENCE_RESPONSE",
                payload: {
                  requestingUserId: payload.userId,
                  userId: userId.current,
                  userName: userName.current,
                  color: userColorValue,
                },
              };
              socketRef.current?.send(JSON.stringify(responseMessage));
            }
            break;

          case "USER_PRESENCE_RESPONSE":
            if (payload.requestingUserId === userId.current) {
              // This is a response to our request for current users
              setUsers(
                (prev) =>
                  new Map(
                    prev.set(payload.userId, {
                      name: payload.userName,
                      color: payload.color,
                    })
                  )
              );
            }
            break;

          case "NODE_DRAGGING":
            if (payload.userId !== userId.current) {
              setDraggingNodes(
                (prev) =>
                  new Map(
                    prev.set(`${payload.nodeId}-${payload.userId}`, {
                      nodeId: payload.nodeId,
                      position: payload.position,
                      userId: payload.userId,
                    })
                  )
              );
            }
            break;

          case "NODE_DRAG_END":
            if (payload.userId !== userId.current) {
              setDraggingNodes((prev) => {
                const newMap = new Map(prev);
                newMap.delete(`${payload.nodeId}-${payload.userId}`);
                return newMap;
              });
            }
            break;

          case "NODE_EDIT_START":
            if (payload.userId !== userId.current) {
              setEditingNodes(
                (prev) =>
                  new Map(
                    prev.set(payload.nodeId, {
                      nodeId: payload.nodeId,
                      userId: payload.userId,
                      userName: payload.userName,
                      userColor: payload.userColor,
                      editType: payload.editType,
                    })
                  )
              );
            }
            break;

          case "NODE_EDIT_END":
            if (payload.userId !== userId.current) {
              setEditingNodes((prev) => {
                const newMap = new Map(prev);
                newMap.delete(payload.nodeId);
                return newMap;
              });
            }
            break;

          case "NODE_TEXT_CHANGED":
            if (payload.userId !== userId.current) {
              const listeners =
                eventListeners.current.get("NODE_TEXT_CHANGED") || [];
              listeners.forEach((listener) => listener(payload));
            }
            break;

          case "CONNECTION_PREVIEW":
            if (payload.userId !== userId.current) {
              setActiveConnections(
                (prev) =>
                  new Map(
                    prev.set(payload.userId, {
                      from: payload.from,
                      to: payload.to,
                      userId: payload.userId,
                    })
                  )
              );
            }
            break;

          case "CONNECTION_PREVIEW_END":
            if (payload.userId !== userId.current) {
              setActiveConnections((prev) => {
                const newMap = new Map(prev);
                newMap.delete(payload.userId);
                return newMap;
              });
            }
            break;

          // ADD THESE: Handle emoji reaction events
          case "EMOJI_REACTION_ADDED":
            if (payload.addedBy !== userId.current) {
              // Trigger custom event listeners for emoji reactions
              const listeners =
                eventListeners.current.get("EMOJI_REACTION_ADDED") || [];
              listeners.forEach((listener) => listener(payload));
            }
            break;

          case "EMOJI_REACTION_REMOVED":
            if (payload.removedBy !== userId.current) {
              // Trigger custom event listeners for emoji reactions
              const listeners =
                eventListeners.current.get("EMOJI_REACTION_REMOVED") || [];
              listeners.forEach((listener) => listener(payload));
            }
            break;

          case "USER_JOINED":
            setUsers(
              (prev) =>
                new Map(
                  prev.set(payload.userId, {
                    name: payload.userName,
                    color: payload.color,
                  })
                )
            );
            break;

          case "USER_LEFT":
            setCursors((prev) => {
              const newCursors = new Map(prev);
              newCursors.delete(payload.userId);
              return newCursors;
            });
            setUsers((prev) => {
              const newUsers = new Map(prev);
              newUsers.delete(payload.userId);
              return newUsers;
            });
            setDraggingNodes((prev) => {
              const newMap = new Map(prev);
              for (const [key, value] of prev) {
                if (value.userId === payload.userId) {
                  newMap.delete(key);
                }
              }
              return newMap;
            });
            setActiveConnections((prev) => {
              const newMap = new Map(prev);
              newMap.delete(payload.userId);
              return newMap;
            });
            setEditingNodes((prev) => {
              const newMap = new Map(prev);
              for (const [key, value] of prev) {
                if (value.userId === payload.userId) {
                  newMap.delete(key);
                }
              }
              return newMap;
            });
            break;
          case "COMMENT_ADDED":
            if (payload.addedBy !== userId.current) {
              const listeners =
                eventListeners.current.get("COMMENT_ADDED") || [];
              listeners.forEach((listener) => listener(payload));
            }
            break;

          case "COMMENT_REACTION_ADDED":
            if (payload.addedBy !== userId.current) {
              const listeners =
                eventListeners.current.get("COMMENT_REACTION_ADDED") || [];
              listeners.forEach((listener) => listener(payload));
            }
            break;

          case "COMMENT_REACTION_REMOVED":
            if (payload.removedBy !== userId.current) {
              const listeners =
                eventListeners.current.get("COMMENT_REACTION_REMOVED") || [];
              listeners.forEach((listener) => listener(payload));
            }
            break;

          default:
            // Trigger custom event listeners
            const listeners = eventListeners.current.get(type) || [];
            listeners.forEach((listener) => listener(payload));
        }
      } catch (error) {
        console.error("Error parsing WebSocket message:", error);
      }
    };

    ws.onclose = () => {
      setConnected(false);
      setSocket(null);
      socketRef.current = null;

      // Clear all user-related states
      setCursors(new Map());
      setDraggingNodes(new Map());
      setActiveConnections(new Map());
      setEditingNodes(new Map());
      setUsers(new Map());
    };

    ws.onerror = (error) => {
      console.error("WebSocket error:", error);
    };

    // Send leave message before closing
    const handleBeforeUnload = () => {
      if (socketRef.current?.readyState === WebSocket.OPEN) {
        const leaveMessage: WebSocketMessage = {
          action: "sendMessage",
          sprintId,
          type: "USER_LEFT",
          payload: {
            userId: userId.current,
          },
        };
        socketRef.current.send(JSON.stringify(leaveMessage));
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      handleBeforeUnload();
      ws.close();
      window.removeEventListener("beforeunload", handleBeforeUnload);

      // Clean up all timeouts
      if (cleanupTimeoutRef.current) {
        clearTimeout(cleanupTimeoutRef.current);
      }

      // Clear all preview states
      setDraggingNodes(new Map());
      setActiveConnections(new Map());
      setCursors(new Map());
    };
  }, [sprintId, userColorValue]);

  // Generic emit function
  const emit = useCallback(
    (type: string, payload: any) => {
      if (socketRef.current?.readyState === WebSocket.OPEN) {
        const message: WebSocketMessage = {
          action: "sendMessage",
          sprintId,
          type,
          payload,
        };
        socketRef.current.send(JSON.stringify(message));
      }
    },
    [sprintId]
  );

  // Generic listener function
  const on = useCallback((type: string, callback: (payload: any) => void) => {
    const listeners = eventListeners.current.get(type) || [];
    listeners.push(callback);
    eventListeners.current.set(type, listeners);

    // Return cleanup function
    return () => {
      const currentListeners = eventListeners.current.get(type) || [];
      const index = currentListeners.indexOf(callback);
      if (index > -1) {
        currentListeners.splice(index, 1);
        eventListeners.current.set(type, currentListeners);
      }
    };
  }, []);

  // Update cursor position
  const updateCursorPosition = useCallback(
    (position: { x: number; y: number }) => {
      sendCursorPosition.current(position);
    },
    []
  );

  // Update node drag position
  const updateNodeDragPosition = useCallback(
    (nodeId: string, position: { x: number; y: number }) => {
      sendNodeDragPosition.current(nodeId, position);
    },
    []
  );

  // Update text changes in real-time
  const updateTextChange = useCallback((nodeId: string, text: string) => {
    sendTextChange.current(nodeId, text);
  }, []);

  // End node dragging
  const endNodeDrag = useCallback(
    (nodeId: string) => {
      if (socketRef.current?.readyState === WebSocket.OPEN) {
        const message: WebSocketMessage = {
          action: "sendMessage",
          sprintId,
          type: "NODE_DRAG_END",
          payload: {
            nodeId,
            userId: userId.current,
          },
        };
        socketRef.current.send(JSON.stringify(message));
      }

      // Also immediately clean up local dragging state
      setDraggingNodes((prev) => {
        const newMap = new Map(prev);
        // Remove all entries for this specific node
        for (const [key, value] of prev) {
          if (value.nodeId === nodeId) {
            newMap.delete(key);
          }
        }
        return newMap;
      });
    },
    [sprintId]
  );

  return {
    socket,
    connected,
    cursors,
    users,
    draggingNodes,
    activeConnections,
    editingNodes,
    emit,
    on,
    updateCursorPosition,
    updateNodeDragPosition,
    updateTextChange,
    endNodeDrag,
    sendConnectionPreview,
    endConnectionPreview,
    startNodeEditing,
    endNodeEditing,
    sendEmojiReactionAdd,
    sendEmojiReactionRemove,
    sendCommentAdd,
    sendCommentReactionAdd,
    sendCommentReactionRemove,
    userId: userId.current,
    userName: userName.current,
    userColor: userColorValue,
  };
};
